import React, { useEffect, useState } from "react";
import { setJourneyToken } from "@/lib/journeyToken";
import { useRecoilState } from "recoil";
import { useSearchParams, useNavigate } from "react-router-dom";

import { useTheme } from "@/b2b/context/ThemeContext";
import { partnerJourneys, navigationRules } from "@/b2b/config/partnerJourney";
import { motion } from "framer-motion";
import { trackConsent } from "@/lib/apis";
import { headingVariants } from "@/lib/data";
import Header from "@/b2b/components/Header";
import Div100vh from "react-div-100vh";
import PrimaryButton from "@/b2b/components/PrimaryButton.jsx";
import { CONSENT_MESSAGES, ALT_PHONE_COPY } from "@/lib/data";
import DynamicInputForm from "@/b2b/components/DynamicInputForm";
import BureauNumbersCard from "@/b2b/components/BureauNumbersCard";
import { onboardingFormDataAtom } from "@/recoil/atom/onboardingFormDataAtom";
import { handleAndTrackError, getDataB2B } from "@/lib/helper";
import {
  storeBureauPhoneHints,
  readBureauPhoneHints,
  readDisplayHints,
  formatHintsForDisplay,
  matchesAnyHint,
} from "@/lib/bureauPhoneHints";

const B2BDynamicScreen = ({ step }) => {
  const navigate = useNavigate();
  const partner = localStorage.getItem("partnerIdentifier");

  const theme = useTheme();

  // const [searchParams] = useSearchParams();
  // const partner = searchParams.get("partner") || "default";
  // const step = searchParams.get("step") || "eligibility-check";

  const [formData, setFormData] = useRecoilState(onboardingFormDataAtom);
  const [disableNextBtn, setDisableNextBtn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Loading...");

  const [error, setError] = useState("");

  // DB-driven partners have no hardcoded journey → fall back to the canonical
  // CirclePe journey so the dynamic steps (eligibility-check, alt-phone) work.
  const journey =
    partnerJourneys[partner] || partnerJourneys.circlepe || [];
  const screenConfig = journey?.find((screen) => screen.step === step);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // Every phone number the bureau reported for this journey. Scoped by journey token so two
  // leads opened in one browser can't validate against each other's numbers.
  //
  // Read on every render rather than memoised: the source is localStorage, which is mutated
  // outside React by `storeBureauPhoneHints` after each submit. Memoising on `[step]` looked
  // cheaper but went stale on a second submit from the SAME step — the list is rewritten while
  // `step` never changes, so the sub-heading and the pre-submit check would both keep using
  // the previous attempt's numbers. Parsing a 3-element array is far cheaper than that class
  // of bug.
  const bureauPhoneHints = readBureauPhoneHints(
    localStorage.getItem("user_identifier"),
  );
  const bureauPhoneHintText = formatHintsForDisplay(bureauPhoneHints);

  // What we SHOW. Falls back to the pre-upgrade global `extractedNumber` when this journey has
  // no stored list, so customers who were already on this screen when the new build shipped
  // still see their number instead of a blank prompt. Deliberately NOT used for validation —
  // that stays on `bureauPhoneHints` above, because the legacy key is global and could belong
  // to a different lead in the same browser. See readDisplayHints for the full reasoning.
  const displayHints = readDisplayHints(localStorage.getItem("user_identifier"));
  const isAltPhoneStep = step === "alt-phone";

  // The mismatch screen gets its own copy (ALT_PHONE_COPY) instead of the configured strings.
  // The old wording — "Phone Number Mismatch" + "...ending with " — read like a rejection and
  // never told the customer WHY we were asking, so they could not tell whether they had been
  // declined. It also dangled when the bureau named no numbers. Every other step keeps using
  // its own config unchanged.
  const headingText = isAltPhoneStep
    ? ALT_PHONE_COPY.heading
    : screenConfig?.heading;

  // One short sentence. The numbers live in BureauNumbersCard below so they can be scanned
  // rather than hunted for inside a paragraph.
  const subHeadingText = isAltPhoneStep
    ? ALT_PHONE_COPY.body
    : screenConfig?.subHeading;

  // The alt-phone field's configured label is "Alt Phone" — internal shorthand that the input
  // was rendering to the customer as "Enter Your Alt Phone". Overriding the placeholder here
  // keeps the fix in one place instead of editing the label in 27 partner configs.
  const altPhoneAwareFields = isAltPhoneStep
    ? screenConfig?.fields?.map((field) =>
        field.name === "alt_phone"
          ? {
              ...field,
              placeholder: ALT_PHONE_COPY.inputPlaceholder(
                displayHints.length > 0,
              ),
            }
          : field,
      )
    : screenConfig?.fields;

  const startOnboardingJourney = async () => {
    try {
      const token = localStorage.getItem("user_identifier");
      const result = await getDataB2B("b2b/onboard/start", token);
      setJourneyToken(result?.token);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  const handleNext = async () => {
    // On the mismatch screen, check the entered number against the bureau's candidates
    // BEFORE spending a billable pull. `matchesAnyHint` accepts a match on ANY of them, and
    // returns true when the bureau named none (~2% of mismatches) — never block on missing
    // data, since that turns a recoverable case into the dead end we are removing.
    if (step === "alt-phone" && !matchesAnyHint(formData?.alt_phone, bureauPhoneHints)) {
      setError(
        ALT_PHONE_COPY.mismatchError(bureauPhoneHintText),
      );
      return;
    }

    if (screenConfig.validateText?.(formData)) {
      setError("Please enter correct details.");
    } else if (screenConfig.validate(formData) && !disableNextBtn) {
      try {
        setLoading((prevState) => !prevState);
        const token = localStorage.getItem("user_identifier");
        const requiredFormData = {};

        if (step === "alt-phone") {
          requiredFormData["pan_number"] = localStorage.getItem("pan_number");
        }
        // if (screenId === "alt-phone") {
        //   requiredFormData["pan_number"] = pdata["pan_number"];
        //   requiredFormData["pincode"] = pdata["pincode"];
        // }
        screenConfig.fields.forEach((field) => {
          if (formData.hasOwnProperty(field.name)) {
            requiredFormData[field.name] = formData[field.name];
          }
        });

        let response = await screenConfig.onSubmit(
          requiredFormData,
          screenConfig.apiPath,
          screenConfig.click_name,
          screenConfig.click_source,
          token,
        );
        const platform =
          navigator?.userAgentData?.platform ||
          navigator.platform ||
          localStorage.getItem("UTM_SOURCE");

        Promise.allSettled([
          trackConsent(
            "Onclick",
            "PAN AND BUREAU Consent",
            "",
            platform,
            CONSENT_MESSAGES.PAN_AND_BUREAU,
          ),
        ]).then((results) => {
          console.log("Consent API calls settled:", results);
        });

        switch (screenConfig.apiPath) {
          case "approval/check":
            if (response.success) {
              if (response?.data?.isEligible) {
                navigate(
                  `/b/${localStorage.getItem(
                    "user_identifier",
                  )}?partner=${partner}&step=${
                    navigationRules[partner]?.panChecked ||
                    navigationRules.default.isEligibleCongrats
                  }`,
                );
              } else {
                navigate(
                  `/b/${localStorage.getItem(
                    "user_identifier",
                  )}?partner=${partner}&step=${
                    navigationRules[partner]?.notHardEligible ||
                    navigationRules.default.notHardEligible
                  }`,
                );
              }
            } else {
              if (response?.data?.status === "PHONE_MISMATCH") {
                // Store EVERY number the bureau named, not just the first. Equifax reports
                // up to three candidates and 52% of mismatches carry more than one, so
                // showing only [0] left the rest unguessable and the customer stuck.
                storeBureauPhoneHints(token, response?.data?.extractedNumbers);
                navigate(
                  `/b/${localStorage.getItem(
                    "user_identifier",
                  )}?partner=${partner}&step=${
                    navigationRules[partner]?.phoneMismatch ||
                    navigationRules.default.phoneMismatch
                  }`,
                );
              } else if (
                [
                  "NEED_PAN",
                  "VALIDATION_ERROR",
                  "BUREAU_PULL_FAILED",
                  "INPUT_ERROR",
                  "CONFLICT",
                  "ERROR",
                ].includes(response?.data?.status)
              ) {
                // Vendor/input error or missing PAN — NOT a credit rejection.
                // Throw back to the PAN screen so the check re-runs.
                navigate(
                  `/b/${localStorage.getItem(
                    "user_identifier",
                  )}?partner=${partner}&step=${
                    navigationRules[partner]?.panNotChecked ||
                    navigationRules.default.panNotChecked
                  }`,
                );
              }
            }
            break;
          case "b2b/onboard/check-eligibility":
            await new Promise((resolve) => setTimeout(resolve, 1000));
            // todo activate it later.
            // await trackConsent(
            //   "Onclick",
            //   "PAN and Bureau",
            //   formData.pan_number,
            //   platform,
            //   CONSENT_MESSAGES.PAN_AND_BUREAU
            // );
            if (response.status === "PHONE_MISMATCH") {
              storeBureauPhoneHints(token, response.extractedNumbers);
              navigate(
                `/b/${localStorage.getItem(
                  "user_identifier",
                )}?partner=${partner}&step=${
                  navigationRules[partner]?.phoneMismatch ||
                  navigationRules.default.phoneMismatch
                }`,
              );
            } else if (
              [
                "NEED_PAN",
                "VALIDATION_ERROR",
                "BUREAU_PULL_FAILED",
                "INPUT_ERROR",
                "CONFLICT",
                "ERROR",
              ].includes(response.status)
            ) {
              // Vendor/input error or missing PAN — NOT a credit rejection.
              // Throw back to the PAN screen so the check re-runs.
              navigate(
                `/b/${localStorage.getItem(
                  "user_identifier",
                )}?partner=${partner}&step=${
                  navigationRules[partner]?.panNotChecked ||
                  navigationRules.default.panNotChecked
                }`,
              );
            } else if (response.isEligible) {
              navigate(`/b/${localStorage.getItem("user_identifier")}`);
            } else {
              navigate(
                `/b/${localStorage.getItem(
                  "user_identifier",
                )}?partner=${partner}&step=${
                  navigationRules[partner]?.notHardEligible ||
                  navigationRules.default.notHardEligible
                }`,
              );
            }
            break;
        }

        // navigate(
        //   `/b/${localStorage.getItem(
        //     "user_identifier"
        //   )}?partner=${partner}&step=${screenConfig.nextPath}`
        // );
      } catch (err) {
        console.log(err);

        handleAndTrackError(error, {
          apiPath: screenConfig.apiPath,
        });
      } finally {
        setLoading((prevState) => !prevState);
      }
    } else if (disableNextBtn) setError("Please try again after some time!");
    else {
      setError("Please enter all required details!");
    }
    // if (screenConfig?.nextPath) {
    //   navigate(
    //     `/b/${localStorage.getItem(
    //       "user_identifier"
    //     )}?partner=${partner}&step=${screenConfig.nextPath}`
    //   );
    // }
  };

  useEffect(() => {
    if (loading) {
      const loadingMessages = [
        "Loading...",
        "Almost there...",
        "Finishing up...",
        "Almost done...",
      ];
      let index = 0;

      // Change loading text at intervals
      const interval = setInterval(() => {
        index = (index + 1) % loadingMessages.length;
        setLoadingText(loadingMessages[index]);
      }, 3000); // Change text every 1 second

      // Clear interval on unmount or when loading stops
      return () => clearInterval(interval);
    }
  }, [loading]);

  useEffect(() => {
    if (error) {
      setError("");
    }
    setDisableNextBtn(false);
  }, [formData]);

  // No config for this step (e.g. an unknown step for a fallback journey) —
  // render nothing instead of crashing on screenConfig.* access below.
  if (!screenConfig) return null;

  if (theme) {
    return (
      <Div100vh
        className={`flex flex-col justify-start p-8 sm:p-8 gap-6 ${theme?.backgroundColor} ${theme?.text}`}
      >
        <div className="flex flex-col gap-8">
          <Header />
          <div className="flex flex-col gap-1">
            <motion.h1
              className="reveal-heading"
              initial="hidden"
              animate="visible"
              variants={headingVariants}
            >
              <span
                className={`heading mb-heading-subheading ${theme?.headingColor}`}
              >
                {headingText}
              </span>
            </motion.h1>
            {/* Gate on the resolved text, not on screenConfig: the mismatch screen supplies its
                own copy, so a partner whose config happens to omit `subHeading` must still get
                the explanation rather than a bare heading. */}
            {subHeadingText && (
              <motion.h1
                className="reveal-heading"
                initial="hidden"
                animate="visible"
                variants={headingVariants}
              >
                <span
                  className={`${screenConfig.subheadingSize || "text-xs"} ${
                    theme?.subHeadingColor
                  }`}
                >
                  {subHeadingText}
                </span>
              </motion.h1>
            )}
          </div>

          {/* The numbers the customer has to act on — given their own card so they are the
              visual anchor of the screen rather than five digits buried in body text. */}
          {isAltPhoneStep && (
            <BureauNumbersCard hints={displayHints} theme={theme} />
          )}
          <DynamicInputForm
            formConfig={altPhoneAwareFields}
            formData={formData}
            setFormData={setFormData}
            setError={setError}
            setDisableNextBtn={setDisableNextBtn}
            setButtonLoading={setLoading}
          />
        </div>
        <div className="flex flex-col h-full items-end justify-end gap-1">
          {step === "eligibility-check" && (
            <span className={`text-3xs ${theme?.textColor} text-center m-2`}>
              {CONSENT_MESSAGES.PAN_AND_BUREAU}
            </span>
          )}

          {/* Same slot the eligibility screen uses for its consent line — fine print belongs
              next to the action, not crowding the explanation at the top. */}
          {isAltPhoneStep && (
            <span className={`text-3xs ${theme?.textColor} text-center m-2`}>
              {ALT_PHONE_COPY.reassurance}
            </span>
          )}

          <PrimaryButton
            text={loading ? loadingText : screenConfig.click_name || "Next"}
            loading={loading}
            handleClick={handleNext}
            error={error}
            disabled={loading || error !== "" || disableNextBtn}
            // className={theme.primary}
          />

          {/* <div
            className={`w-full text-center text-[0.678rem] ${
              theme?.text || "text-[#aeaeae]"
            }`}
          >
            powered by{" "}
            <a
              target="_blank"
              href="https://fexprime.com"
              className="hover:underline hover:text-primary"
            >
              Fexprime
            </a>
          </div> */}
        </div>
      </Div100vh>
    );
  }
  return null;
};

export default B2BDynamicScreen;
